import { defineBehavior } from "../../core/composition/define-behavior.js";
import { effect } from "../../core/signals/effect.js";
import { getMinBufferedEnd } from "../../media/dom/mse/duration.js";
import { listen } from "@videojs/utils/dom";
//#region src/playback/behaviors/dom/recover-end-stall.ts
/**
* Recover the end-of-stream stall that Chrome exhibits on skewed A/V. After `endOfStream`, when the audio and video
* tracks end a few ms apart (e.g. a source with an A/V PTS skew), Chrome's audio-clock-paced playback freezes the
* playhead ~50–70ms short of the reachable buffered end and never fires `ended` — so playback hangs at the very end and
* loop never re-triggers. This behavior watches for the `waiting` event that fires at that freeze and, when the
* MediaSource is `ended` and the playhead sits at the reachable buffered end, nudges `currentTime` to `duration` to
* force the native `ended`.
*
* **Event-driven, no poll.** `waiting` fires at the instant the playhead stalls (measured ~0ms latency), so there's
* nothing to gain from polling — and polling would add its interval + a stall threshold before reacting.
*
* **Proximity to the _reachable_ buffered end** is the discriminator. That end is
* `getMinBufferedEnd(mediaSource.sourceBuffers)` — the `min` of the per-track (video/audio) SourceBuffer ends, i.e. the
* furthest point playback can reach — read from the SourceBuffers directly rather than the `mediaElement.buffered`
* aggregate. Once `endOfStream` is signalled that's the true content end. Requiring the playhead within
* `endStallNudgeWindow` of it distinguishes the real end-of-stream freeze from a mid-stream buffer-hole stall (which
* sits far from the buffered end), so we never skip content. The window must exceed the freeze gap; too small would
* miss the stall (a permanent hang), so the default is generous relative to the measured gap and is config-tunable for
* empirical tuning.
*
* Inert where it shouldn't act: live (the MediaSource never reaches `ended` while the window grows; `duration` is
* `Infinity`) and streams that end cleanly (no `waiting`).
*
* See `internal/decisions/spf/end-of-stream-av-skew-recovery.md`.
*/
/** ~2.8× the measured max freeze gap (71ms) — tight, but with headroom against a miss. */
const DEFAULT_END_STALL_NUDGE_WINDOW = .2;
/**
* Whether a `waiting` should be forced to `ended`: the MediaSource is `ended`, the stream is finite (not live),
* playback is active (not paused/seeking/already-ended), and the playhead sits within `nudgeWindow` of the reachable
* buffered end (so it's the true end, not a mid-stream buffer hole). Pure — the behavior supplies the live values.
*/
function shouldForceEnded(input, nudgeWindow) {
	const { msEnded, durationFinite, paused, seeking, ended, currentTime, bufferedEnd } = input;
	if (!msEnded || !durationFinite || paused || seeking || ended || bufferedEnd === void 0) return false;
	const gap = bufferedEnd - currentTime;
	return bufferedEnd > 0 && gap >= 0 && gap < nudgeWindow;
}
function recoverEndStallSetup({ context, config }) {
	const nudgeWindow = config?.endStallNudgeWindow ?? .2;
	return effect(() => {
		const mediaElement = context.mediaElement.get();
		if (!mediaElement) return;
		const onWaiting = () => {
			const mediaSource = context.mediaSource.get();
			if (shouldForceEnded({
				msEnded: mediaSource?.readyState === "ended",
				durationFinite: Number.isFinite(mediaElement.duration),
				paused: mediaElement.paused,
				seeking: mediaElement.seeking,
				ended: mediaElement.ended,
				currentTime: mediaElement.currentTime,
				bufferedEnd: mediaSource ? getMinBufferedEnd(mediaSource.sourceBuffers) : void 0
			}, nudgeWindow)) mediaElement.currentTime = mediaElement.duration;
		};
		return listen(mediaElement, "waiting", onWaiting);
	});
}
const recoverEndStall = defineBehavior({
	stateKeys: [],
	contextKeys: ["mediaElement", "mediaSource"],
	setup: recoverEndStallSetup
});
//#endregion
export { DEFAULT_END_STALL_NUDGE_WINDOW, recoverEndStall };
